import { useState, useEffect } from 'react';
import { purchaseReportingApi } from '../../services/purchaseReportingApi';
import type { PRFilters, PRVendorRow, PaginationInfo } from '../../types/purchaseReporting';

interface Props {
    filters: PRFilters;
    refreshKey: number;
    onNavigateToDetail: (filterUpdate: Partial<PRFilters>) => void;
}

const PAGE_SIZE = 25;

function formatNumber(n: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
}

const SORT_COLUMNS: Record<string, string> = {
    vendorCode: 'VendorCode',
    vendorName: 'VendorName',
    vendorCountry: 'CountryISO3',
    qtyReceived: 'QtyReceived',
    totalCostLCY: 'TotalCostLCY',
    receipts: 'Receipts',
};

export function ByVendorTab({ filters, refreshKey, onNavigateToDetail }: Props) {
    const [items, setItems] = useState<PRVendorRow[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState('qtyReceived');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [page, setPage] = useState(1);

    useEffect(() => {
        setPage(1);
    }, [filters, refreshKey]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        purchaseReportingApi.getByVendor({
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
            company: filters.company || undefined,
            businessUnit: filters.businessUnit || undefined,
            locationID: filters.locationID || undefined,
            sortBy,
            sortOrder,
            page,
            limit: PAGE_SIZE,
        })
            .then(res => {
                setItems(res.items);
                setPagination(res.pagination);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [filters, sortBy, sortOrder, page, refreshKey]);

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(field);
            setSortOrder('DESC');
        }
        setPage(1);
    };

    const sortIcon = (field: string) => {
        if (sortBy !== field) return '';
        return sortOrder === 'ASC' ? ' \u2191' : ' \u2193';
    };

    if (loading && items.length === 0) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (error) return <div className="text-red-600 p-4">{error}</div>;

    return (
        <div className="space-y-4">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b bg-gray-50">
                            {Object.keys(SORT_COLUMNS).map(key => (
                                <th
                                    key={key}
                                    onClick={() => handleSort(key)}
                                    className="px-4 py-2 text-left font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                                >
                                    {{
                                        vendorCode: 'Code',
                                        vendorName: 'Vendor',
                                        vendorCountry: 'Country',
                                        qtyReceived: 'Qty Received',
                                        totalCostLCY: 'Cost (LCY)',
                                        receipts: 'Receipts',
                                    }[key]}{sortIcon(key)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row, i) => (
                            <tr
                                key={`${row.vendorCode}-${i}`}
                                className="border-b hover:bg-blue-50 cursor-pointer"
                                onClick={() => onNavigateToDetail({ vendorID: `${filters.company || ''}-${row.vendorCode}`.replace(/^-/, '') })}
                            >
                                <td className="px-4 py-2 text-gray-500">{row.vendorCode}</td>
                                <td className="px-4 py-2 font-medium">{row.vendorName}</td>
                                <td className="px-4 py-2">{row.vendorCountry}</td>
                                <td className="px-4 py-2 text-right font-mono">{formatNumber(row.qtyReceived)}</td>
                                <td className="px-4 py-2 text-right font-mono">{formatNumber(row.totalCostLCY)}</td>
                                <td className="px-4 py-2 text-right">{formatNumber(row.receipts)}</td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No data for selected filters</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>
                        {formatNumber(pagination.total)} vendors | Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => p - 1)}
                            disabled={!pagination.hasPrev}
                            className="px-3 py-1 border rounded disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={!pagination.hasNext}
                            className="px-3 py-1 border rounded disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
